/**
 * 个人页面 (My Page) - 复用Home的AppShell框架
 * 显示用户的个人信息、视频管理和浏览历史
 */

import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { getAuthHeaders, clearAuth, getToken, saveAuth, getUser, type User } from '../utils/auth';
import type { ApiResponse } from '../types/api';
import { UserInfoCard, MyStickyTabs } from './MyStickyHeader';
import { MyVideoList } from './MyVideoList';
import './MyPage.css';
import './common/ListStatus.css';
import ListStatusFooter from './common/ListStatusFooter';

const MyPage: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  const getTabFromSearch = () => {
    const tab = new URLSearchParams(location.search).get('tab');
    return tab === 'history' ? 'history' : 'management';
  };

  const [activeTab, setActiveTab] = useState<'management' | 'history'>(() => getTabFromSearch());
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [delayedLoading, setDelayedLoading] = useState(false);

  const navigate = useNavigate();
  const scrollElRef = useRef<HTMLElement | null>(null);
  const myVideoListRef = useRef<any>(null);

  // 初始化滚动容器引用
  useEffect(() => {
    scrollElRef.current = document.querySelector('.app-shell-body') as HTMLElement | null;
  }, []);

  // 处理tab切换由 MyStickyHeader 触发硬刷新（通过 URL query），这里仅用于保持 hasMore when navigating via history
  const handleTabChange = (tab: 'management' | 'history') => {
    // noop for hard-reload behavior; kept for compatibility if needed
    setHasMore(true);
  };

  // 如果地址栏的 query 改变（后退/前进等），同步 activeTab 到 state
  useEffect(() => {
    const next = new URLSearchParams(location.search).get('tab') === 'history' ? 'history' : 'management';
    setActiveTab(next);
  }, [location.search]);

  // 延迟显示 loading（避免瞬间切换闪烁）
  useEffect(() => {
    let timer: number | null = null;
    if (listLoading) {
      timer = window.setTimeout(() => setDelayedLoading(true), 350);
    } else {
      setDelayedLoading(false);
    }
    return () => {
      if (timer !== null) {
        clearTimeout(timer);
      }
    };
  }, [listLoading]);

  useEffect(() => {
    let didCancel = false;
    (async () => {
      try {
        const currentUser = getUser();
        if (currentUser) {
          // 如果本地已有用户信息，优先使用，避免额外请求 /auth/me（例如刚刚上传后已更新本地缓存）
          setUser(currentUser);
          if (!didCancel) {
            setLoading(false);
          }
          return;
        }

        // Do not attach an AbortController signal here to avoid accidental cancellation
        // caused by component lifecycle; rely on didCancel gating to ignore stale results.
        const response = await fetch(`${API_BASE_URL}/auth/me`, {
          headers: getAuthHeaders(),
        });

        if (response.status === 401) {
          clearAuth();
          navigate('/login');
          return;
        }

        if (!response.ok) {
          throw new Error('获取用户信息失败');
        }

        const data: ApiResponse<User> = await response.json();
        if (data.success && data.data) {
          if (!didCancel) {
            setUser(data.data);
            // 更新 localStorage
            const token = getToken();
            if (token) {
              saveAuth({ token, user: data.data });
            }
          }
        }
      } catch (err) {
        // No AbortError expected since we don't attach a signal; log other errors.
        console.error('加载用户信息失败:', err);
      } finally {
        if (!didCancel) {
          setLoading(false);
        }
      }
    })();
    return () => {
      didCancel = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="loading">加载中...</div>
    );
  }

  return (
    <>
      {/* User Info Card - Scrollable */}
      <div className="my-user-info-section">
        <UserInfoCard user={user} />
      </div>

      {/* Sticky Tabs - Always visible when scrolling */}
      <div className="my-sticky-tabs-container">
        <MyStickyTabs activeTab={activeTab} />
      </div>

      {/* Main Content - 复用Home的video-grid结构 */}
      <div className="video-grid">
        <MyVideoList
          ref={myVideoListRef}
          activeTab={activeTab}
          user={user}
          hasMore={hasMore}
          loadingMore={loadingMore}
          onHasMoreChange={setHasMore}
          onLoadingMoreChange={setLoadingMore}
          scrollElRef={scrollElRef}
          onEmptyStateChange={setIsEmpty}
          onListLoadingChange={setListLoading}
        />
      </div>

      {/* 空/加载状态提示 - 在video-grid外面，居中显示 */}
      {delayedLoading ? (
        <div className="empty-state">
          <p>加载中...</p>
        </div>
      ) : isEmpty ? (
        <div className="empty-state">
          <p>{activeTab === 'management' ? '暂无视频' : '暂无浏览历史'}</p>
        </div>
      ) : null}

      {/* 加载状态 - 在video-grid外面，与首页结构一致 */}
      <ListStatusFooter
        loadingMore={loadingMore}
        hasMore={hasMore}
        allowNoMore={(!loading && !isEmpty && !delayedLoading)}
      />
    </>
  );
};

export default MyPage;
